import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Server, Plus, Activity, Cpu, HardDrive, Wifi, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const healthIcons = {
  healthy: <CheckCircle className="w-5 h-5 text-accent" />,
  degraded: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
  down: <XCircle className="w-5 h-5 text-destructive" />
};

const envColors = {
  production: 'bg-accent/20 text-accent border-accent/30',
  staging: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
  dev: 'bg-primary/20 text-primary border-primary/30'
};

export default function EnvironmentsPage() {
  const { getAuthHeaders } = useAuth();
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEnv, setNewEnv] = useState({
    name: '',
    env_type: 'staging',
    description: ''
  });

  const fetchEnvironments = async () => {
    try {
      const response = await axios.get(`${API_URL}/environments`, {
        headers: getAuthHeaders()
      });
      setEnvironments(response.data);
    } catch (error) {
      console.error('Failed to fetch environments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnvironments();
  }, [getAuthHeaders]);

  const handleCreateEnvironment = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/environments`, newEnv, {
        headers: getAuthHeaders()
      });
      setEnvironments([...environments, response.data]);
      setCreateDialogOpen(false);
      setNewEnv({ name: '', env_type: 'staging', description: '' });
      toast.success('Environment created successfully!');
    } catch (error) {
      toast.error('Failed to create environment');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="environments-title">
              Environments
            </h1>
            <p className="text-foreground/60">Manage your deployment environments</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-bold hover:shadow-neon-cyan" data-testid="create-env-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Environment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" aria-describedby="create-env-description">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Create Environment</DialogTitle>
                <p id="create-env-description" className="sr-only">Form to create a new deployment environment.</p>
              </DialogHeader>
              <form onSubmit={handleCreateEnvironment} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Environment Name</Label>
                  <Input
                    value={newEnv.name}
                    onChange={(e) => setNewEnv({ ...newEnv, name: e.target.value })}
                    placeholder="e.g., Production EU"
                    className="bg-background border-border"
                    required
                    data-testid="env-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Type</Label>
                  <Select
                    value={newEnv.env_type}
                    onValueChange={(value) => setNewEnv({ ...newEnv, env_type: value })}
                  >
                    <SelectTrigger className="bg-background border-border" data-testid="env-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dev">Development</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Description</Label>
                  <Input
                    value={newEnv.description}
                    onChange={(e) => setNewEnv({ ...newEnv, description: e.target.value })}
                    placeholder="Brief description"
                    className="bg-background border-border"
                    data-testid="env-description-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary text-black font-bold" data-testid="create-env-submit">
                  Create Environment
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Environments Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : environments.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-16 text-center">
              <Server className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-heading font-semibold text-white mb-2">No Environments Yet</h3>
              <p className="text-muted-foreground mb-6">Create your first environment to get started</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-primary text-black font-bold">
                <Plus className="w-5 h-5 mr-2" />
                Create Environment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {environments.map((env, i) => (
              <motion.div
                key={env.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {healthIcons[env.health_status]}
                        <div>
                          <CardTitle className="text-white font-heading">{env.name}</CardTitle>
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full border capitalize ${envColors[env.env_type] || envColors.staging}`}>
                            {env.env_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {env.description && (
                      <p className="text-sm text-muted-foreground mb-4">{env.description}</p>
                    )}
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Activity className="w-4 h-4" /> Request Rate
                        </span>
                        <span className="text-foreground font-code">{env.metrics?.request_rate || 0}/min</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Cpu className="w-4 h-4" /> CPU Usage
                        </span>
                        <span className="text-foreground font-code">{env.metrics?.cpu_usage || 0}%</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <HardDrive className="w-4 h-4" /> Memory
                        </span>
                        <span className="text-foreground font-code">{env.metrics?.memory_usage || 0} GB</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <Wifi className="w-4 h-4" /> Response Time
                        </span>
                        <span className="text-foreground font-code">{env.metrics?.response_time || 0}ms</span>
                      </div>
                    </div>

                    {env.current_version && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground">Current Version</p>
                        <p className="text-sm text-primary font-code">{env.current_version}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
